/**
 * Add Last-Minute Trip Dialog - Phase 7
 * Preview-only dialog for checking if new trip can fit
 * Shows three-tier result: zero-disruption / local-repair / failed
 */

import LocationOffIcon from "@mui/icons-material/LocationOff";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addHours, format } from "date-fns";
import React, { useState } from "react";
import {
  Aircraft,
  PlacementWithLocation,
  Ramp,
  Stack as StackType,
} from "../../../types";
import { Tenant } from "../../../services/StackService";
import {
  LastMinuteInsertionRampResult,
  runLastMinuteInsertionOnRamp,
} from "../TemporalTest/temporalRampIntegration";
import { findSpotForPosition } from "../TemporalTest/positionMapping";

export interface AddLastMinuteTripDialogProps {
  open: boolean;
  onClose: () => void;
  ramp: Ramp;
  stack: StackType;
  placements: PlacementWithLocation[];
  currentTime?: Date;
}

export function AddLastMinuteTripDialog({
  open,
  onClose,
  ramp,
  stack,
  placements,
  currentTime,
}: AddLastMinuteTripDialogProps) {
  const defaultStart = currentTime || new Date();
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [startTime, setStartTime] = useState<Date>(defaultStart);
  const [endTime, setEndTime] = useState<Date>(addHours(defaultStart, 4));
  const [maxDisruption, setMaxDisruption] = useState(2);
  const [previewResult, setPreviewResult] =
    useState<LastMinuteInsertionRampResult | null>(null);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedTenant(null);
      setStartTime(currentTime || new Date());
      setEndTime(addHours(currentTime || new Date(), 4));
      setMaxDisruption(2);
      setPreviewResult(null);
    }
  }, [open, currentTime]);

  // Auto-populate times when tenant selected
  React.useEffect(() => {
    if (selectedTenant && selectedTenant.start_time && selectedTenant.end_time) {
      try {
        setStartTime(new Date(selectedTenant.start_time));
        setEndTime(new Date(selectedTenant.end_time));
      } catch {
        // Invalid times in database, keep current values
      }
    }
  }, [selectedTenant]);

  // Get tenant options from stack (both placed and unplaced)
  const tenantOptions = React.useMemo(() => {
    return stack.tenants.filter((t) => t.aircraft != null);
  }, [stack.tenants]);

  const handlePreview = () => {
    if (!selectedTenant || !selectedTenant.aircraft) return;

    // Validate time range
    if (startTime >= endTime) {
      alert("Start time must be before end time");
      return;
    }

    try {
      const result = runLastMinuteInsertionOnRamp(
        ramp,
        stack,
        {
          aircraft: selectedTenant.aircraft,
          startTime,
          endTime,
          trip_id: selectedTenant.id,  // Ensure placement key matches lookup key
        },
        {
          maxDisruption,
          startTime: currentTime || new Date(),
          horizonHours: 24,
        }
      );

      setPreviewResult(result);
    } catch (error) {
      console.error("[AddLastMinuteTrip] Preview error:", error);
      alert(
        `Preview failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  // Helper: Get tail number from aircraft ID
  const getTailNumber = (aircraftId: string): string => {
    const placement = placements.find((p) => p.id === aircraftId);
    return placement?.tail_number || aircraftId;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Last-Minute Trip - Preview</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Step 1: Select Aircraft */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Aircraft
              </Typography>
              <Autocomplete
                value={selectedTenant}
                onChange={(_, value) => setSelectedTenant(value)}
                options={tenantOptions}
                getOptionLabel={(tenant) =>
                  `${tenant.tail_number} - ${tenant.aircraft?.model || tenant.aircraft?.icao || "Unknown"}`
                }
                renderOption={(props, tenant) => {
                  // Calculate status info for this tenant
                  let statusLabel = "Unplaced";
                  let statusColor: "default" | "info" | undefined = undefined;
                  let statusVariant: "filled" | "outlined" = "outlined";
                  let statusIcon = <LocationOffIcon />;

                  if (tenant.position.x !== null && tenant.position.y !== null) {
                    // Find which spot tenant is in
                    const spotId = findSpotForPosition(tenant.position, ramp.shapes, ramp.depth);

                    // Look up spot name from shapes (name stored in .text property)
                    const spot = ramp.shapes.find(s => s.id === spotId);
                    const spotName = spot?.text || 'Spot Unknown';

                    // Format times (HH:mm format)
                    try {
                      const startDate = new Date(tenant.start_time);
                      const endDate = new Date(tenant.end_time);

                      // Check if dates are valid
                      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                        statusLabel = spotName;
                      } else {
                        const startFormatted = format(startDate, 'HH:mm');
                        const endFormatted = format(endDate, 'HH:mm');
                        statusLabel = `${spotName} • ${startFormatted}-${endFormatted}`;
                      }
                    } catch (error) {
                      // Error formatting - just show spot
                      statusLabel = spotName;
                    }

                    statusColor = "info";
                    statusVariant = "outlined";
                    statusIcon = <LocationOnIcon />;
                  }

                  return (
                    <li {...props} key={tenant.id}>
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {tenant.tail_number} - {tenant.aircraft?.model || tenant.aircraft?.icao || "Unknown"}
                          </Typography>
                        </Box>
                        <Chip
                          label={statusLabel}
                          size="small"
                          color={statusColor}
                          variant={statusVariant}
                          icon={statusIcon}
                        />
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select aircraft..." />
                )}
                fullWidth
              />
            </Box>

            {/* Step 2: Select Times */}
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Arrival Time
                </Typography>
                <DateTimePicker
                  value={startTime}
                  onChange={(newValue) => newValue && setStartTime(newValue)}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Departure Time
                </Typography>
                <DateTimePicker
                  value={endTime}
                  onChange={(newValue) => newValue && setEndTime(newValue)}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
            </Stack>

            {/* Step 3: Max Disruption */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Max Aircraft Moves: {maxDisruption}
              </Typography>
              <Slider
                value={maxDisruption}
                onChange={(_, val) => setMaxDisruption(val as number)}
                min={0}
                max={5}
                step={1}
                marks={[
                  { value: 0, label: "Zero" },
                  { value: 2, label: "2" },
                  { value: 5, label: "Max" },
                ]}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* Preview Button */}
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={!selectedTenant}
              fullWidth
            >
              Check Availability
            </Button>

            {/* Step 4: Preview Results */}
            {previewResult && (
              <Box>
                {previewResult.insertionAnalysis.tier === "zero-disruption" && (() => {
                  // Extract spot name from placement result
                  const placement = previewResult.insertionAnalysis.placement?.[selectedTenant?.id || ''];
                  const spotId = placement && 'spotId' in placement ? placement.spotId : null;
                  const spot = ramp.shapes.find(s => s.id === spotId);
                  const spotName = spot?.text || 'Spot Unknown';

                  return (
                    <Alert severity="success">
                      <Typography variant="body2" fontWeight={500}>
                        Can fit in <strong>{spotName}</strong> with ZERO disruption!
                      </Typography>
                      <Typography variant="caption">
                        {selectedTenant?.tail_number} can be placed without
                        moving any existing aircraft.
                      </Typography>
                    </Alert>
                  );
                })()}

                {previewResult.insertionAnalysis.tier === "local-repair" && (
                  <Alert severity="warning">
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Requires moving{" "}
                      {previewResult.insertionAnalysis.totalMoves} aircraft:
                    </Typography>
                    <List dense sx={{ mt: 1 }}>
                      {previewResult.insertionAnalysis.disruptedAircraft.map(
                        (id) => (
                          <ListItem key={id} sx={{ py: 0 }}>
                            <ListItemText
                              primary={getTailNumber(id)}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        )
                      )}
                    </List>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: "block" }}
                    >
                      These aircraft would need to be moved to make space for{" "}
                      {selectedTenant?.tail_number}.
                    </Typography>
                  </Alert>
                )}

                {previewResult.insertionAnalysis.tier === "failed" && (
                  <Alert severity="error">
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Cannot fit within {maxDisruption} move
                      {maxDisruption !== 1 ? "s" : ""}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {previewResult.insertionAnalysis.analysis.failureReason}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Try: Increase max disruption, adjust arrival/departure
                      times, or use smaller aircraft.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
